/**
 * MermaidRenderer — AI 生成的 Mermaid 图表渲染器
 *
 * 将 ```mermaid 代码块渲染为交互式 SVG 图表。
 * 支持：时间轴、流程图、关系图、序列图、甘特图等。
 *
 * 用法：在 ReactMarkdown 的 components.code 中，
 * 当检测到 language-mermaid 时，渲染此组件。
 */

import React, { useEffect, useRef, useState, useId } from 'react';
import mermaid from 'mermaid';

// 初始化 Mermaid（全局只需一次）
let mermaidInitialized = false;
const initMermaid = () => {
    if (mermaidInitialized) return;
    mermaid.initialize({
        startOnLoad: false,
        theme: 'neutral',
        securityLevel: 'loose',
        fontFamily: '"Inter", "Noto Sans SC", sans-serif',
        flowchart: { useMaxWidth: true, htmlLabels: true },
        timeline: { useMaxWidth: true },
        er: { useMaxWidth: true },
        sequence: { useMaxWidth: true },
    });
    mermaidInitialized = true;
};

interface MermaidRendererProps {
    chart: string;
}

const MermaidRenderer: React.FC<MermaidRendererProps> = ({ chart }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [svgContent, setSvgContent] = useState<string>('');
    const [error, setError] = useState<string>('');
    const uniqueId = useId().replace(/:/g, '_');

    useEffect(() => {
        const renderChart = async () => {
            if (!chart.trim()) return;
            initMermaid();

            try {
                const { svg } = await mermaid.render(`mermaid-${uniqueId}`, chart.trim());
                setSvgContent(svg);
                setError('');
            } catch (err: any) {
                console.error('[Mermaid] Render failed:', err);
                setError(err?.message || 'Failed to render diagram');
                setSvgContent('');
            }
        };

        renderChart();
    }, [chart, uniqueId]);

    if (error) {
        // 当发生错误（通常是因为大模型流式输出代码尚不完整）
        // 不要显示刺眼的红色报错弹窗，而是退化为一个优雅的灰色代码块
        return (
            <div style={{
                margin: '12px 0',
                padding: '16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                position: 'relative',
            }}>
                <div style={{ position: 'absolute', top: '8px', right: '12px', fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>
                    MERMAID
                </div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {chart}
                </pre>
            </div>
        );
    }

    if (!svgContent) {
        return (
            <div style={{
                margin: '12px 0',
                padding: '16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: '13px',
            }}>
                正在渲染图表...
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            style={{
                margin: '16px 0',
                padding: '16px',
                background: '#FAFAFA',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                overflow: 'auto',
                maxWidth: '100%',
            }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
        />
    );
};

export default MermaidRenderer;
